import styled from 'styled-components';
import { colors, radius } from '../../../../styles/tokens';

export const Container = styled.div`
  padding: 40px;

  @media (max-width: 1199px) {
    padding: 20px;
  }
`;

export const BackLink = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;

  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-bottom: 16px;

  font-size: 14px;
  font-weight: 600;
  color: ${colors.onSurfaceVariant};

  &:hover {
    color: ${colors.primary};
  }
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 28px;
`;

export const PageTitle = styled.h2`
  font-size: 32px;
  font-weight: 800;
  color: ${colors.onSurface};
  letter-spacing: -0.02em;
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

export const Discard = styled.button`
  padding: 0 24px;
  height: 44px;

  background-color: transparent;
  border-radius: ${radius.DEFAULT};
  border: 1px solid ${colors.outlineVariant};
  cursor: pointer;

  font-size: 12px;
  font-weight: 700;
  color: ${colors.onSurfaceVariant};
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:hover {
    color: ${colors.onSurface};
  }
`;

export const Save = styled.button`
  padding: 0 24px;
  height: 44px;

  background-color: ${colors.primary};
  border-radius: ${radius.DEFAULT};
  border: none;
  cursor: pointer;

  font-size: 12px;
  font-weight: 700;
  color: ${colors.onPrimary};
  text-transform: uppercase;
  letter-spacing: 0.05em;

  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const Card = styled.div`
  background-color: ${colors.surfaceContainerLow};
  border-radius: ${radius.lg};
  padding: 32px;
  margin-bottom: 24px;
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;

  h3 {
    font-size: 15px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${colors.onSurface};
    white-space: nowrap;
  }

  div.ln {
    flex: 1;
    height: 1px;
    background-color: ${colors.outlineVariant}30;
  }
`;

export const FieldsRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

export const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.onSurfaceVariant};
  margin-bottom: 8px;

  span.req {
    color: ${colors.error};
  }
`;

export const TextField = styled.input`
  height: 48px;
  padding: 0 16px;

  background-color: ${colors.surfaceContainer};
  border: 1px solid transparent;
  border-radius: ${radius.sm};

  font-size: 14px;
  color: ${colors.onSurface};

  &::placeholder {
    color: ${colors.onSurfaceVariant};
  }

  &:focus {
    outline: none;
    border-color: ${colors.primary}80;
  }
`;

export const SelectField = styled.select`
  height: 48px;
  padding: 0 16px;

  background-color: ${colors.surfaceContainer};
  border: 1px solid transparent;
  border-radius: ${radius.sm};

  font-size: 14px;
  color: ${colors.onSurface};

  &:focus {
    outline: none;
    border-color: ${colors.primary}80;
  }
`;

export const SplitTabsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 28px;
`;

export const SplitTabGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

interface SplitTabProps {
  active?: boolean;
}

export const SplitTab = styled.button<SplitTabProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;

  padding: 10px 18px;
  min-width: 96px;
  max-width: 160px;

  background-color: ${({ active }) =>
    active ? `${colors.primary}1f` : colors.surfaceContainer};
  border: 1px solid ${({ active }) => (active ? colors.primary : 'transparent')};
  border-radius: ${radius.sm};
  cursor: pointer;

  b {
    font-size: 13px;
    font-weight: 800;
    color: ${({ active }) => (active ? colors.primary : colors.onSurface)};
  }

  span {
    font-size: 11px;
    color: ${colors.onSurfaceVariant};
  }

  small {
    max-width: 100%;
    font-size: 9px;
    font-weight: 600;
    color: ${colors.onSurfaceVariant};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const AddSplitTab = styled.button`
  width: 44px;
  height: 44px;

  display: flex;
  align-items: center;
  justify-content: center;

  background: transparent;
  border: 2px dashed ${colors.outlineVariant}80;
  border-radius: ${radius.sm};
  cursor: pointer;

  &:hover {
    border-color: ${colors.primary}80;
  }
`;

export const EditorGrid = styled.div`
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  gap: 32px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

export const SplitEditorColumn = styled.div`
  min-width: 0;
`;

export const LibraryColumn = styled.div`
  min-width: 0;
`;

export const SplitHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

export const SplitHeadLabel = styled.p`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${colors.onSurfaceVariant};
  margin-bottom: 2px;
`;

export const SplitHeadTitle = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: ${colors.onSurface};
`;

export const DeleteSplitButton = styled.button`
  flex-shrink: 0;
  padding: 6px;

  display: flex;
  align-items: center;
  justify-content: center;

  background-color: transparent;
  border: none;
  border-radius: ${radius.sm};
  cursor: pointer;

  color: ${colors.error};

  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.error}1a;
  }
`;

export const FieldsGrid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
`;

export const SmallLabel = styled.p`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${colors.onSurfaceVariant};
  margin-bottom: 12px;
`;

export const ExerciseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const ExerciseItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  padding: 12px 14px;
  background-color: ${colors.surfaceContainer};
  border-radius: ${radius.sm};
`;

export const ExerciseOrder = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  color: ${colors.onSurfaceVariant};
`;

export const ExerciseInfo = styled.div`
  flex: 1;
  min-width: 0;

  p.name {
    font-size: 13px;
    font-weight: 700;
    color: ${colors.onSurface};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  p.group {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${colors.primary};
  }
`;

export const RemoveExerciseButton = styled.button`
  flex-shrink: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  color: ${colors.onSurfaceVariant};

  &:hover {
    color: ${colors.error};
  }
`;

export const EmptySplit = styled.div`
  height: 100%;
  border: 2px dashed ${colors.outlineVariant}40;
  border-radius: ${radius.md};
  padding: 40px 20px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  text-align: center;

  color: ${colors.onSurfaceVariant};

  p {
    font-size: 13px;
  }
`;

export const LibraryFiltersRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`;

export const LibrarySearch = styled.div`
  flex: 1;
  min-width: 0;

  display: flex;
  align-items: center;
  gap: 8px;

  background-color: ${colors.surfaceContainer};
  padding: 12px 16px;
  border-radius: ${radius.sm};

  input {
    flex: 1;
    min-width: 0;
    background: transparent;
    border: none;
    font-size: 13px;
    color: ${colors.onSurface};

    &:focus {
      outline: none;
    }

    &::placeholder {
      color: ${colors.onSurfaceVariant};
    }
  }
`;

export const LibraryGroupSelect = styled(SelectField)`
  flex-shrink: 0;
  width: 180px;
  padding-right: 36px;

  /*
   * padding-right sozinho não afasta o chevron nativo da borda -- o
   * navegador desenha essa seta com uma margem própria, fixa,
   * ignorando o padding do elemento. Precisa desligar a aparência
   * nativa e desenhar a seta via background-image pra controlar a
   * distância até a borda.
   */
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23${colors.onSurfaceVariant.slice(
    1,
  )}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
`;

export const LIBRARY_SCROLL_HEIGHT = '350px';

export const LibraryScroll = styled.div`
  height: ${LIBRARY_SCROLL_HEIGHT};
  overflow-y: auto;
  padding-right: 4px;
`;

export const SplitExercisesScroll = styled.div`
  height: ${LIBRARY_SCROLL_HEIGHT};
  overflow-y: auto;
  padding-right: 4px;
`;

export const LibraryCardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: 1300px) {
    grid-template-columns: 1fr;
  }
`;

interface LibraryCardProps {
  active?: boolean;
}

export const LibraryCard = styled.div<LibraryCardProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  padding: 12px 14px;
  border-radius: ${radius.sm};
  cursor: pointer;

  background-color: ${({ active }) =>
    active ? `${colors.success}1f` : colors.surfaceContainer};
  border: 1px solid
    ${({ active }) => (active ? `${colors.success}60` : 'transparent')};

  transition: border-color 0.2s, background-color 0.2s;

  &:hover {
    border-color: ${({ active }) =>
      active ? `${colors.success}60` : `${colors.primary}60`};
  }
`;

export const LibraryCardInfo = styled.div`
  min-width: 0;

  p.name {
    font-size: 13px;
    font-weight: 700;
    color: ${colors.onSurface};
  }

  span.group {
    display: inline-block;
    margin-top: 6px;
    padding: 2px 8px;
    border-radius: ${radius.sm};
    background-color: ${colors.surfaceContainerHigh};

    font-size: 9px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${colors.onSurfaceVariant};
  }
`;

export const LibraryCardToggle = styled.div<LibraryCardProps>`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: ${radius.full};

  display: flex;
  align-items: center;
  justify-content: center;

  background-color: ${({ active }) =>
    active ? colors.success : colors.surfaceContainerHigh};
`;
